// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import {
  Pill,
  Sans12,
  Sans14,
  Sans16,
  spacing,
  TooltipTrigger,
} from "@recidiviz/design-system";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import { useState } from "react";
import styled from "styled-components";

import { Button, Icon, palette } from "~design-system";

import LocationIcon from "../../assets/static/images/locationPin.svg?react";
import PhoneIcon from "../../assets/static/images/phone.svg?react";
import TagsIcon from "../../assets/static/images/tags.svg?react";
import { RoutePlannerClientEvent } from "../../RootStore/AnalyticsStore/AnalyticsStore";
import { Client, SupervisionTask } from "../../WorkflowsStore";
import { usePersonTracking } from "../hooks/usePersonTracking";
import {
  TooltipRow,
  TooltipSection,
  TooltipSectionDetails,
  TooltipSectionHeader,
} from "../sharedComponents";
import { InfoButton } from "../WorkflowsJusticeInvolvedPersonProfile/InfoButton";
import { WorkflowsTooltip } from "../WorkflowsTooltip";
import { RoutePlannerClientsPresenter } from "./RoutePlannerClientsPresenter";
import { CheckboxContents, EmptyCheckbox, NumberedCheckbox } from "./styles";

const BorderedClientCard = styled.div<{
  $selected: boolean;
  $selectable: boolean;
  $isMobile: boolean;
  $waiting: boolean;
}>`
  border-radius: ${rem(8)};
  padding: ${rem(14)} ${rem(16)};

  display: flex;
  flex-direction: row;

  border: 1px solid;

  ${({ $selectable, $selected }) =>
    $selectable &&
    `border-color: ${$selected ? palette.slate50 : palette.slate20};
      background-color: ${$selected ? palette.marble3 : palette.marble1};
      transition: all ease 200ms;
    `}

  ${({ $selectable, $selected, $isMobile }) =>
    $selectable &&
    !$isMobile &&
    `&:hover,
     &:focus {
        border-color: ${$selected ? palette.slate80 : palette.slate50};
        background-color: ${$selected ? palette.marble5 : palette.marble2};
        cursor: pointer;
      }`}

  ${({ $selectable }) =>
    !$selectable &&
    `
      border-color: ${palette.slate15};
      background-color: ${palette.marble3};
      cursor: not-allowed;
    `}

  ${({ $waiting }) =>
    $waiting &&
    `
      cursor: wait !important;
      * {
        cursor: wait !important;
      }
    `}
`;

const ClientInfo = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
`;

const NameRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const Name = styled(Sans16)`
  color: ${palette.pine4};
  margin-right: ${rem(6)};
`;

const SupervisionLevel = styled(Sans12)`
  display: flex;
  align-items: center;
  padding: 0 ${rem(spacing.sm)};

  font-weight: 400;
  color: ${palette.pine1};
  background-color: ${palette.slate10};
  border: 1px solid ${palette.slate20};
  border-radius: ${rem(2)};
`;

const AdditionalInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(spacing.xs)};
`;

const JustifiedInfo = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(6)};

  & svg {
    flex-shrink: 0;
  }
`;

const TasksRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${rem(6)};

  & svg {
    flex-shrink: 0;
  }
`;

const SmallInfoText = styled(Sans14)`
  color: ${palette.pine1};
  font-weight: 400;
`;

const NoAddressFoundText = styled(Sans14)`
  color: ${palette.slate50};
  font-weight: 400;
`;

const SmallInfoLink = styled(Sans14)`
  color: ${palette.signal.selected};
  &:hover,
  &:focus,
  &:active {
    color: ${palette.signal.notification};
    text-decoration: underline;
  }
`;

const SchedulingBadge = styled(Pill).attrs({
  filled: true,
  textColor: palette.pine1,
})`
  height: ${rem(21)};
  width: fit-content;
  font-size: ${rem(12)};
  font-weight: 400;
`;

const CallButton = styled(Button).attrs({ shape: "block" })`
  margin-left: auto;

  display: flex;
  gap: ${rem(spacing.sm)};
`;

const Caret = styled(Icon)`
  margin-top: ${rem(4)};
`;

function SupervisionLevelTooltip({ copy }: { copy: string }) {
  return (
    <TooltipSection>
      <TooltipSectionHeader>{copy}</TooltipSectionHeader>
      <TooltipRow>
        <TooltipSectionDetails>Supervision Level</TooltipSectionDetails>
      </TooltipRow>
    </TooltipSection>
  );
}

// A collapsed version of the ClientCard, only for use on mobile
const CollapsedClientCard = observer(function ClientCard({
  tasks,
  presenter,
  onDropdownClick,
  person,
}: {
  tasks: SupervisionTask[] | undefined;
  presenter: RoutePlannerClientsPresenter;
  onDropdownClick: () => void;
  person: Client;
}) {
  const { supervisionLevelShort, supervisionTooltip } =
    presenter.getClientCardCopy(tasks, person);

  const isSelected = presenter.isPersonSelected(person);
  const ordinalRank = presenter.indexOfPerson(person) + 1;

  const hasAddress = person.formattedAddress !== undefined;
  const hasBadAddress = presenter.hasBadAddress(person);
  const isSelectable = hasAddress && !hasBadAddress;

  return (
    <BorderedClientCard
      $selectable={isSelectable}
      $selected={isSelected}
      $waiting={presenter.isAddingPerson}
      $isMobile={true}
      onClick={async () => {
        if (!isSelectable) return;

        if (isSelected) {
          if (tasks) {
            presenter.removePerson(person);
          } else presenter.removeAddedPerson(person);
        } else {
          await presenter.addPerson(person);
        }
      }}
    >
      {isSelected ? (
        <NumberedCheckbox>
          <CheckboxContents>{ordinalRank}</CheckboxContents>
        </NumberedCheckbox>
      ) : (
        <EmptyCheckbox $selectable={isSelectable} />
      )}

      <ClientInfo className="fs-exclude">
        <NameRow>
          <Name>{presenter.displayName(person)}</Name>
          <WorkflowsTooltip
            person={person}
            contents={<SupervisionLevelTooltip copy={supervisionTooltip} />}
          >
            <SupervisionLevel>{supervisionLevelShort}</SupervisionLevel>
          </WorkflowsTooltip>
        </NameRow>
      </ClientInfo>
      <Caret
        kind={"DownChevron"}
        size={12}
        rotate={270}
        onClick={(e) => {
          // Prevent clicks from also selecting the person's card
          e.stopPropagation();
          onDropdownClick();
        }}
      />
    </BorderedClientCard>
  );
});

export const ClientCard = observer(function ClientCard({
  tasks,
  presenter,
  isMobile,
  person,
}: {
  tasks?: SupervisionTask[];
  presenter: RoutePlannerClientsPresenter;
  isMobile: boolean;
  person: Client;
}) {
  const [collapsed, setCollapsed] = useState(true);

  usePersonTracking(person, () => {
    presenter.trackRoutePlannerClientEvent(
      RoutePlannerClientEvent.Surfaced,
      person,
    );
  });

  const { supervisionLevelShort, supervisionTooltip, tasksInfo } =
    presenter.getClientCardCopy(tasks, person);

  const isSelected = presenter.isPersonSelected(person);

  const ordinalRank = presenter.indexOfAllPeople(person) + 1;

  const hasAddress = person.formattedAddress !== undefined;
  const hasBadAddress = presenter.hasBadAddress(person);
  const isSelectable = hasAddress && !hasBadAddress;

  if (collapsed && isMobile) {
    return (
      <CollapsedClientCard
        person={person}
        tasks={tasks ? tasks : undefined}
        presenter={presenter}
        onDropdownClick={() => {
          presenter.trackRoutePlannerClientEvent(
            RoutePlannerClientEvent.CardToggledMobile,
            person,
          );
          setCollapsed(false);
        }}
      />
    );
  }

  return (
    <BorderedClientCard
      $selectable={isSelectable}
      $selected={isSelected}
      $isMobile={isMobile}
      $waiting={presenter.isAddingPerson}
      onClick={async () => {
        if (!isSelectable) return;

        if (isSelected) {
          if (tasks) {
            presenter.removePerson(person);
          } else presenter.removeAddedPerson(person);
        } else {
          await presenter.addPerson(person);
        }
      }}
    >
      {isSelected ? (
        <NumberedCheckbox>
          <CheckboxContents>{ordinalRank}</CheckboxContents>
        </NumberedCheckbox>
      ) : (
        <EmptyCheckbox $selectable={isSelectable} />
      )}

      <ClientInfo className="fs-exclude">
        <NameRow>
          <Name>{presenter.displayName(person)}</Name>
          <WorkflowsTooltip
            person={person}
            contents={<SupervisionLevelTooltip copy={supervisionTooltip} />}
          >
            <SupervisionLevel>{supervisionLevelShort}</SupervisionLevel>
          </WorkflowsTooltip>
        </NameRow>

        <AdditionalInfo>
          <InfoRow>
            <LocationIcon />

            {person.formattedAddress ? (
              <>
                <a
                  href={presenter.mapsAddressLink(person.formattedAddress)}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    // Prevent link clicks from also selecting the person's card
                    e.stopPropagation();

                    presenter.trackRoutePlannerClientEvent(
                      RoutePlannerClientEvent.AddressClicked,
                      person,
                    );
                  }}
                >
                  <SmallInfoLink>{person.address}</SmallInfoLink>
                </a>
                {hasBadAddress && (
                  <TooltipTrigger
                    contents={presenter.getBadAddressCopy()}
                    maxWidth={340}
                  >
                    <InfoButton infoUrl={undefined} />
                  </TooltipTrigger>
                )}
              </>
            ) : (
              <NoAddressFoundText>
                {presenter.getNoAddressFoundCopy()}
              </NoAddressFoundText>
            )}
          </InfoRow>
          {tasksInfo &&
            tasksInfo.map((task) => (
              <TasksRow>
                <InfoRow>
                  <TagsIcon />
                  <SmallInfoText>{task.type}</SmallInfoText>
                </InfoRow>
                {task.scheduledStatus && (
                  <SchedulingBadge
                    color={
                      task.isScheduled ? palette.slate10 : "rgb(244, 233, 215)"
                    }
                  >
                    {task.scheduledStatus}
                  </SchedulingBadge>
                )}
              </TasksRow>
            ))}
        </AdditionalInfo>

        <JustifiedInfo>
          {isMobile && person.phoneNumber && (
            <a
              href={person.phoneNumberUri}
              onClick={(e) => {
                // Prevent link clicks from also selecting the person's card
                e.stopPropagation();

                presenter.trackRoutePlannerClientEvent(
                  RoutePlannerClientEvent.PhoneCalledMobile,
                  person,
                );
              }}
            >
              <CallButton>
                <PhoneIcon fill={palette.marble1} /> Call
              </CallButton>
            </a>
          )}
        </JustifiedInfo>
      </ClientInfo>

      {person.phoneNumber && !isMobile && (
        <TooltipTrigger
          className="fs-exclude"
          contents={`Phone number: ${person.phoneNumber}`}
        >
          <PhoneIcon fill={palette.pine4} />
        </TooltipTrigger>
      )}

      {isMobile && (
        <Caret
          kind={"DownChevron"}
          size={12}
          onClick={(e) => {
            // Prevent clicks from also selecting the person's card
            e.stopPropagation();
            presenter.trackRoutePlannerClientEvent(
              RoutePlannerClientEvent.CardToggledMobile,
              person,
            );
            setCollapsed(true);
          }}
        />
      )}
    </BorderedClientCard>
  );
});
